import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "../../lib/utils";

/**
 * Mount ONCE near the app root. Tooltips share this provider's hover-intent state: the first
 * tooltip waits `delayDuration` (so the pointer merely passing across a toolbar opens nothing),
 * then for `skipDelayDuration` after one closes, the next opens instantly (so deliberately
 * moving along a row of icons feels immediate). Per-tooltip providers would defeat the skip
 * window — that's what made an icon rail spray a trail of tooltips during pointer travel.
 */
function TooltipProvider({
  delayDuration = 300,
  skipDelayDuration = 300,
  // Our tooltips are plain labels, never hover targets. With hoverable content (Radix's
  // default), leaving the trigger does NOT close the tooltip — the mounted content's grace-area
  // tracking does. A `Tooltip` whose content is conditionally unmounted (e.g. the sidebar labels,
  // rendered only when collapsed) then opens invisibly and never closes: collapsing the sidebar
  // after brushing every button revealed all of them at once. Trigger-leave-closes fixes that.
  disableHoverableContent = true,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      skipDelayDuration={skipDelayDuration}
      disableHoverableContent={disableHoverableContent}
      {...props}
    />
  );
}

/** Requires a `TooltipProvider` ancestor (mounted at the app root). */
function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        // Hover-revealed triggers (e.g. a row kebab that is display:none until row hover) can
        // disappear while their tooltip is still fading out; the popper then re-measures a zero
        // rect and the dying tooltip flashes at the viewport origin. Hide it the moment its
        // trigger stops being measurable instead.
        hideWhenDetached
        className={cn(
          "bg-foreground text-background animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-3 py-1.5 text-xs text-balance",
          className
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="bg-foreground fill-foreground z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
